import type { Stream } from 'src/services/LeadStreamsService/agent';

export type StreamStatusKey = 'active' | 'paused' | 'ended' | 'pending';

export function hasActiveStreamAccess(stream?: Stream | null) {
    if (!stream) {
        return false;
    }

    if (stream.has_active_access) {
        return true;
    }

    if (!stream.active_access_offer_kind || !stream.access_expires_at) {
        return false;
    }

    const expiresAt = new Date(stream.access_expires_at).getTime();
    return Number.isFinite(expiresAt) && expiresAt > Date.now();
}

export function getStreamApprovedDeliveries(stream?: Stream | null) {
    return Math.max(0, stream?.approved_deliveries || 0);
}

export function getStreamPackageSize(stream?: Stream | null) {
    return Math.max(0, stream?.package_size || 0);
}

export function getStreamBalance(stream?: Stream | null) {
    return Math.max(0, stream?.balance || 0);
}

export function getStreamTotalSize(stream?: Stream | null): number | null {
    if (!stream || hasActiveStreamAccess(stream)) {
        return null;
    }

    const packageSize = getStreamPackageSize(stream);
    const balance = getStreamBalance(stream);

    return packageSize > 0 ? packageSize : balance;
}

export function hasPaidStreamHistory(stream?: Stream | null) {
    if (!stream) {
        return false;
    }

    return (
        hasActiveStreamAccess(stream) ||
        !!stream.active_access_offer_kind ||
        getStreamBalance(stream) > 0 ||
        getStreamPackageSize(stream) > 0 ||
        getStreamApprovedDeliveries(stream) > 0
    );
}

export function isStreamReadyForPayment(stream?: Stream | null) {
    if (!stream || hasPaidStreamHistory(stream) || (stream.status && stream.status !== 'pending')) return false;
    const profile = stream.filter_profile;
    const hasRule = (rules?: string[]) => rules?.some((rule) => typeof rule === 'string' && rule.trim());
    return Boolean(
        stream.category?.id > 0 &&
            (!profile?.category?.id || profile.category.id === stream.category.id) &&
            (profile?.customer_description?.trim() || profile?.offer?.trim()) &&
            hasRule(profile?.target_intents) &&
            hasRule(profile?.exclude)
    );
}

export function shouldOpenStreamDetails(stream?: Stream | null) {
    return hasPaidStreamHistory(stream) || isStreamReadyForPayment(stream);
}

export function getStreamStatus(stream?: Stream | null): { key: StreamStatusKey; label: string } {
    if (!stream) {
        return { key: 'pending', label: 'Не найден' };
    }

    if (stream.status) {
        const labels: Record<StreamStatusKey, string> = {
            active: 'Активен',
            paused: 'На паузе',
            ended: 'Закончился',
            pending: 'Нужна оплата',
        };
        return {
            key: stream.status,
            label: isStreamReadyForPayment(stream) ? 'Осталось оплатить' : stream.status_label || labels[stream.status],
        };
    }

    if (!hasPaidStreamHistory(stream)) {
        return { key: 'pending', label: isStreamReadyForPayment(stream) ? 'Осталось оплатить' : 'Нужна оплата' };
    }

    if (hasActiveStreamAccess(stream) || getStreamBalance(stream) > 0) {
        return stream.is_active ? { key: 'active', label: 'Активен' } : { key: 'paused', label: 'На паузе' };
    }

    if (
        stream.active_access_offer_kind ||
        getStreamPackageSize(stream) > 0 ||
        getStreamApprovedDeliveries(stream) > 0
    ) {
        return { key: 'ended', label: 'Закончился' };
    }

    return { key: 'pending', label: isStreamReadyForPayment(stream) ? 'Осталось оплатить' : 'Нужна оплата' };
}

export function getStreamStatusColor(status: StreamStatusKey) {
    switch (status) {
        case 'active':
            return '#24A866';
        case 'paused':
            return '#8A99AD';
        case 'ended':
            return '#E05A47';
        default:
            return '#647991';
    }
}

export function formatStreamDate(value?: string | null) {
    if (!value) {
        return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleDateString('ru-RU');
}

export function formatStreamShortDate(value?: string | null) {
    if (!value) {
        return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
    });
}

export function getStreamAccessLabel(stream?: Stream | null) {
    if (!stream) {
        return '';
    }

    if (hasActiveStreamAccess(stream)) {
        const expiresAt = formatStreamDate(stream.access_expires_at);
        return expiresAt ? `Безлимит до ${expiresAt}` : 'Безлимит';
    }

    const balance = getStreamBalance(stream);
    if (balance > 0) {
        return `${balance} лидов осталось`;
    }

    if (getStreamPackageSize(stream) > 0 || getStreamApprovedDeliveries(stream) > 0) {
        return 'Баланс закончился';
    }

    if (stream.active_access_offer_kind) {
        return 'Доступ закончился';
    }

    return 'Нужна оплата';
}

export function getStreamMenuMetricLabel(stream?: Stream | null) {
    if (!stream) {
        return '';
    }

    const status = getStreamStatus(stream);
    if (status.key === 'paused') {
        return 'Пауза';
    }

    if (status.key === 'ended') {
        return 'Закончился';
    }

    if (hasActiveStreamAccess(stream)) {
        const expiresAt = formatStreamShortDate(stream.access_expires_at);
        return expiresAt ? `∞ до ${expiresAt}` : '∞';
    }

    const balance = getStreamBalance(stream);
    if (balance > 0) {
        return `${new Intl.NumberFormat('ru-RU').format(balance)} осталось`;
    }

    return status.label;
}

export function getStreamDisplayName(stream?: Stream | null) {
    if (!stream) {
        return 'Поток лидов';
    }

    return stream.name || stream.category?.name || 'Поток лидов';
}

export function normalizeStreamDisplayName(value?: string | null) {
    return (value || '').replace(/^📦\s*/u, '').trim();
}

function removeCategoryIcon(value: string, icon?: string | null) {
    const normalizedIcon = (icon || '').trim();
    if (!normalizedIcon || !value.startsWith(normalizedIcon)) {
        return value;
    }

    return value.slice(normalizedIcon.length).trim();
}

function streamNameVariants(stream: Stream) {
    const icon = stream.category?.icon;

    return [stream.name, stream.category?.name, getStreamDisplayName(stream)]
        .flatMap((value) => {
            const normalizedValue = normalizeStreamDisplayName(value);
            return [normalizedValue, removeCategoryIcon(normalizedValue, icon)];
        })
        .filter(Boolean);
}

export function formatLeadStreamSourceName(rawName?: string | null, streams: Stream[] = []) {
    const normalizedRawName = normalizeStreamDisplayName(rawName);
    if (!normalizedRawName) {
        return 'Поток лидов';
    }

    const stream = streams.find((item) => streamNameVariants(item).includes(normalizedRawName));
    if (!stream) {
        return normalizedRawName;
    }

    const icon = (stream.category?.icon || '').trim();
    const displayName = normalizeStreamDisplayName(getStreamDisplayName(stream));

    if (!icon || displayName.startsWith(icon)) {
        return displayName;
    }

    return `${icon} ${displayName}`;
}

export function shouldShowStreamInMenu(stream: Stream) {
    return shouldOpenStreamDetails(stream);
}
